import React from "react";
import { Apple, Smartphone, Check, Droplet } from "lucide-react";
import { APP_LINKS } from "@/lib/site";

export default function AppShowcase() {
  const features = [
    "Quick and easy ordering",
    "Real-time delivery tracking",
    "Secure UPI / cards checkout",
    "Order history & repeat ordering",
  ];
  return (
    <section id="app" className="py-24 md:py-32 bg-white">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="eyebrow">The Wateriia App</div>
            <h2 className="serif h-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-[#0b1d2a]">
              Click. <span className="italic text-[#0369a1]">Order.</span> Delivered.
            </h2>
            <p className="mt-5 text-slate-600 max-w-xl text-base sm:text-lg leading-relaxed">
              Order clean drinking water in seconds. Enjoy seamless ordering, real-time tracking, and dependable delivery — all from your smartphone.
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
              {features.map((f, i) => (
                <li
                  key={i}
                  data-testid={`app-feature-${i}`}
                  className="flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50/40 px-4 py-3"
                >
                  <span className="h-7 w-7 rounded-full bg-[#0ea5e9] text-white flex items-center justify-center">
                    <Check size={14} />
                  </span>
                  <span className="text-sm text-[#0b1d2a]">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={APP_LINKS.ios}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download the Wateriia app on the App Store"
                data-testid="app-cta-appstore"
                className="inline-flex items-center gap-3 rounded-2xl bg-[#0b1d2a] text-white px-5 py-3.5 hover:bg-[#0369a1] transition"
              >
                <Apple size={20} />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
              <a
                href={APP_LINKS.android}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get the Wateriia app on Google Play"
                data-testid="app-cta-play"
                className="inline-flex items-center gap-3 rounded-2xl bg-white border border-sky-100 px-5 py-3.5 hover:border-[#0ea5e9] transition"
              >
                <Smartphone size={20} className="text-[#0369a1]" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
            </div>

            {/* On a laptop the store buttons lead nowhere useful; scanning puts the app on the phone. */}
            <div className="mt-8 hidden md:flex gap-5" data-testid="app-qr-codes">
              {[
                { src: "/app-qr-ios.svg", label: "iPhone", store: "App Store", href: APP_LINKS.ios },
                { src: "/app-qr-android.svg", label: "Android", store: "Google Play", href: APP_LINKS.android },
              ].map((q) => (
                <a
                  key={q.src}
                  href={q.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-2xl border border-sky-100 bg-white p-3 pr-5 hover:border-[#0ea5e9] transition"
                >
                  <img
                    src={q.src}
                    alt={`QR code to download the Wateriia app on the ${q.store}`}
                    width="96"
                    height="96"
                    className="h-24 w-24"
                    loading="lazy"
                  />
                  <div className="leading-tight">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Scan for {q.label}</div>
                    <div className="text-sm font-semibold text-[#0b1d2a]">{q.store}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-6 rounded-[40px] bg-gradient-to-tr from-[#0ea5e9]/20 to-[#0369a1]/10 blur-2xl" />
              <div className="relative phone-frame">
                <div className="phone-screen aspect-[9/19]">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-slate-900/80" />
                  <div className="pt-10 px-5 flex items-center justify-between text-[10px] text-slate-500">
                    <span>Order Live</span>
                    <span>Wateriia</span>
                  </div>

                  <div className="mt-5 mx-4 rounded-2xl bg-[#0b1d2a] text-white p-5 relative overflow-hidden">
                    <div className="flex items-center gap-2">
                      <Droplet size={14} className="text-sky-300" />
                      <span className="text-[10px] uppercase tracking-widest opacity-70">Live Track</span>
                    </div>
                    <div className="mt-2 serif text-2xl">Rider · 800m away</div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="h-1 rounded-full bg-sky-400" />
                      <div className="h-1 rounded-full bg-sky-400" />
                      <div className="h-1 rounded-full bg-white/20" />
                    </div>
                    <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-sky-400/30 blur-2xl" />
                  </div>

                  <div className="mt-3 mx-4 rounded-2xl bg-white border border-sky-100 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Reorder</div>
                        <div className="font-semibold">20L × 2 · 1‑Click</div>
                      </div>
                      <button className="rounded-full bg-[#0ea5e9] text-white text-xs px-3 py-1.5">Buy</button>
                    </div>
                  </div>

                  <div className="mt-3 mx-4 grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="rounded-xl bg-sky-50 p-3 text-center">
                        <Droplet size={14} className="text-[#0ea5e9] mx-auto" />
                        <div className="text-[10px] mt-1 text-slate-500">Plan {n}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
